import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import CreateCampaignPage from "../pages/CreateCampaignPage";
import SelectCampaignMintSpace from "./SelectCampaignMintSpace";
import { getAllMyMintSpaces } from "../api/mintSpace";
import { useImageLoader } from "../hooks/useImageLoader";
import { useTranslation } from "../context/TranslationContext";
import WindowNavPanels from "./WindowNavPanels";

const CampaignWindow = ({ isDark, setToast }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const spaceIdFromQuery = searchParams.get("spaceId");
  const createSpaceFromQuery = searchParams.get("createSpace") === "1";
  const [mintSpaces, setMintSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const containerRef = useRef(null);
  const { imagesLoaded } = useImageLoader(containerRef);

  useEffect(() => {
    if (createSpaceFromQuery) {
      navigate("/create-business", { replace: true });
      return;
    }
    // Editing a campaign has an id and no space to pick.
    setShowCampaignForm(Boolean(id || spaceIdFromQuery));
  }, [id, spaceIdFromQuery, createSpaceFromQuery, location.search, navigate]);

  useEffect(() => {
    const fetchMintSpaces = async () => {
      try {
        const result = await getAllMyMintSpaces();
        if (result.success && result.data) {
          setMintSpaces(result.data || []);
        }
      } catch {
        setMintSpaces([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMintSpaces();
  }, [id]);

  const handleSelectSpace = (spaceId) => {
    navigate(`/create-campaign?spaceId=${spaceId}`);
  };

  const handleCreateBusiness = () => {
    navigate("/create-business");
  };

  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";

  const activeView = showCampaignForm ? "campaign" : "select";


  const renderPanel = (viewId) => {
    switch (viewId) {
      case "campaign":
        return (
          <div className="flex h-full min-h-0 flex-col overflow-hidden p-3 sm:p-4">
            <CreateCampaignPage isDark={isDark} setToast={setToast} />
          </div>
        );
      case "select":
      default:
        return (
          <div className="campaign-window-scrollbar h-full overflow-y-auto">
            <div className="mx-auto w-full max-w-[1112px] p-space-16 lg:p-space-24">
              <SelectCampaignMintSpace
                mintSpaces={mintSpaces}
                loading={loading}
                onSelectSpace={handleSelectSpace}
                onCreateBusiness={handleCreateBusiness}
                t={t}
              />
            </div>
          </div>
        );
    }
  };

  if (loading || !imagesLoaded) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-accent-default border-r-transparent" />
          <div className={`text-lg ${mutedText}`}>{t("windows.loading") || "Loading..."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full" ref={containerRef}>
      <div
        className="flex h-full flex-col overflow-hidden"
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <WindowNavPanels activeKey={activeView} renderPanel={renderPanel} className="flex-1 min-h-0" />
        </div>
      </div>
    </div>
  );
};

export default CampaignWindow;
